// 활동 이력 수동 항목 API — 포털 Mongo 소유(cic 무관).
//
// 계약을 `creatorsApi.ts`에 넣지 않은 이유: 그 파일은 cic 응답 계약 + 혜원 lane 공유 파일이다.
// 원천도 배포 주체도 다른 데이터를 같은 파일에 섞으면 "이 타입이 어디서 오는가"가 흐려진다.
// brandLinksApi.ts 선례를 따라 이 기능의 타입·호출을 한 파일에 자체 보유한다.
//
// 경로가 `/api/creators/` 밖인 이유는 서버 라우트(server/src/routes/notableHistory.ts) 주석 참고 —
// dev에서 `/api/creators*`는 cic BFF로 직결되므로 포털 소유 데이터는 그 네임스페이스를 피한다.
import { ApiError } from '../platform/creatorBusiness'

export interface NotableHistoryEntry {
  id: string
  year: number
  note: string
  sortOrder: number
  createdBy: string
  updatedAt: string
}

export interface NotableHistoryInput {
  year: number
  note: string
  sortOrder?: number
}

const BASE = '/api/notable-history'

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...init,
  })
  type Envelope = { success?: boolean; data?: T; errorMessage?: string }
  let body: Envelope | null = null
  try {
    body = (await res.json()) as Envelope
  } catch {
    /* non-JSON */
  }
  if (res.status === 401) throw new ApiError('로그인이 필요합니다.', 401, true)
  if (!res.ok || !body?.success) {
    // 400은 서버가 사용자에게 보여줄 사유를 담아 보낸다(연도 범위·길이 등) — 그대로 노출한다.
    throw new ApiError(body?.errorMessage ?? '요청을 처리하지 못했습니다.', res.status, !body)
  }
  return body.data as T
}

/** 목록 — 연도 내림차순·같은 해는 sortOrder 오름차순(서버 정렬). */
export async function fetchNotableHistory(creatorId: string): Promise<NotableHistoryEntry[]> {
  const data = await call<{ items: NotableHistoryEntry[] }>(`${BASE}/${encodeURIComponent(creatorId)}`)
  return data.items
}

export async function createNotableHistory(
  creatorId: string,
  input: NotableHistoryInput,
): Promise<NotableHistoryEntry> {
  return call<NotableHistoryEntry>(`${BASE}/${encodeURIComponent(creatorId)}`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

// 수정·삭제는 **크리에이터 스코프 경로**를 쓴다 — 서버가 소유자까지 필터에 걸어야
// 항목 id만으로 남의 이력을 건드리는 경로가 생기지 않는다(소유자 불일치는 404).
export async function updateNotableHistory(
  creatorId: string,
  entryId: string,
  input: NotableHistoryInput,
): Promise<NotableHistoryEntry> {
  return call<NotableHistoryEntry>(
    `${BASE}/${encodeURIComponent(creatorId)}/entries/${encodeURIComponent(entryId)}`,
    { method: 'PUT', body: JSON.stringify(input) },
  )
}

export async function deleteNotableHistory(creatorId: string, entryId: string): Promise<void> {
  await call<unknown>(
    `${BASE}/${encodeURIComponent(creatorId)}/entries/${encodeURIComponent(entryId)}`,
    { method: 'DELETE' },
  )
}
